export enum OverlayScalingAlignmentHorizontal {
    LEFT = "left",
    CENTER = "center",
    RIGHT = "right",
}

export enum OverlayScalingAlignmentVertical {
    UP = "up",
    CENTER = "center",
    DOWN = "down",
}

export type OverlayScalingAlignment = [OverlayScalingAlignmentHorizontal, OverlayScalingAlignmentVertical];

export enum OverlayScalingMode {
    FILL = "fill",
    SCALE_TO_FIT = "scaleToFit",
    SCALE_TO_FILL = "scaleToFill",
}

export interface IThreediumError {
    customError: boolean;
    code: number;
    errorTitle: string;
    errorMessage: string;
    error: string;
    reference: string;
    statusCode: number;
    status: boolean;
}
export interface IThreediumInitLoadingStatus {
    loadedAssets: number;
    loadedSize: number;
    progress: number;
    totalAssets: number;
    totalSize: number;
}
export interface IThreediumInitOptions {
    distID: string;
    solution3DName: string;
    projectName: string;
    solution3DID: string;
    /**
     * ID of html element to which canvas will be attached
     * The rest of the fields are not required, but can be passed to customize behaviour.
     */
    containerID: string;
    /**
     * Called when loading of models and textures has changed
     * First and only parameter is an object contain information about loading status.
     */
    onLoadingChanged?: (loadingStatus: IThreediumInitLoadingStatus) => void;

    /**
     * Called whenever user clicks on the scene
     * First and only parameter is an array of objects in format.
     */
    onPointerClick?: (objects: object[]) => void;

    /** Called when user moves camera using mouse or touch. */
    onCameraInteraction?: () => void;
}

export type ApiCallback<T> = (err: Error, res: T) => void;
export type Options<T> = { [key: string]: T };

export type EnablerFunction = (params: { enable: boolean }, callback: ApiCallback<any>) => void;
export type DistributionInfoFunction = (params: { solution3DID: number }, callback: ApiCallback<any>) => void;
export type CallbackOnlyFunction<T = void> = (callback: ApiCallback<T>) => void;
export type PartsFunction = (params: { parts: Array<string> }, callback: ApiCallback<any>) => void;
export type NameFunction = (params: { name: string }, callback: ApiCallback<any>) => void;
export type MaterialFunction = (params: { material: string }, callback: ApiCallback<any>) => void;
export type OptionsFunction = (
    params: { name: string; position: Array<number>; scale: Array<number>; rotation: Array<number> },
    callback: ApiCallback<any>,
) => void;
export type OverlayEntryFunction = (
    params: { overlay: string; overlayEntry: string },
    callback: ApiCallback<any>,
) => void;
export type PartNamesFunction = (params: { partNames: Array<string> }, callback: ApiCallback<any>) => void;
export type ModelsFunction = (params: { models: Array<string> }) => void;
export type LightsFunction = (params: { lights: Array<string> }) => void;
export type PartsObjectFunction = (
    params: { partObjects: Array<{ parts: Array<string> }> },
    callback: ApiCallback<any>,
) => void;
export type AnnotationsFunction = (params: { annotations: Array<string> }, callback: ApiCallback<any>) => void;
export type CustomPartObjects = (params: { customPartObjects: Array<string> }, callback: ApiCallback<any>) => void;

interface IOverlayOptions {
    enabled?: boolean;
    opacity?: number;
    rotation?: number;
}

export interface IImageOverlayOptions extends IOverlayOptions {
    imageUrl?: string;
    scalingMode?: OverlayScalingMode;
    scalingAlignment?: OverlayScalingAlignment;
}

export type FontWeight =
    | "normal"
    | "bold"
    | "bolder"
    | "lighter"
    | "100"
    | "200"
    | "300"
    | "400"
    | "500"
    | "600"
    | "700"
    | "800"
    | "900";
export type FontStyle = "normal" | "italic" | "oblique";

export interface ITextOverlayOptions extends IOverlayOptions {
    fontUrl?: string;
    text?: string;
    fontColor?: string;
    fontScale?: number;
    fontWeight?: FontWeight;
    fontSpacing?: number;
    fontStyle?: FontStyle;
    scalingMode?: OverlayScalingMode;
    scalingAlignment?: OverlayScalingAlignment;
}

export interface IMaterialOptions {
    color?: string;
    metallness?: number;
    roughness?: number;
    opacity?: number;
    transparent?: boolean;
}

export interface IEntry {
    name: string;
    shortName: string;
    type: string;
}

export interface IOverlay {
    entries: IEntry[];
    name: string;
    shortName: string;
    type: string;
}

interface IMaterial {
    name: string;
    shortName: string;
    type: string;
}

interface ITransition {
    name: string;
    shortName: string;
    targetType: string;
    type: string;
}

interface IPartObject {
    parts: Array<string>;
    material: string;
}

export interface IPartObjects {
    [index: number]: IPartObject;
}

export interface IThreediumAPI {
    activateCamera: (params: { name: string; preventDefault?: boolean }, callback?: ApiCallback<any>) => void;
    activateCameraControl: NameFunction;
    activateDecorationOnParts: (
        params: { parts: Array<string>; decoration?: string },
        callback?: ApiCallback<any>,
    ) => void;
    activateModifier: (params: { modifier: string }) => void;
    activateRenderer: NameFunction;
    activateTransition: (
        params: { transition: string; target?: string; duration?: number; config?: any; callback?: ApiCallback<any> },
        callback?: ApiCallback<any>,
    ) => void;
    activateViewport: NameFunction;
    addEntryToOverlay: (
        params: { overlay: string; overlayEntry: string; index?: number },
        callback: ApiCallback<any>,
    ) => void;
    addSelectable: PartNamesFunction;
    applyMaterialToAllParts: MaterialFunction;
    changeAnnotationMaterial: (
        params: { annotations: Array<string>; material: string },
        callback: ApiCallback<any>,
    ) => void;
    changeBackgroundColor: (
        params: { viewport: string; color?: string; opacity?: string },
        callback: ApiCallback<any>,
    ) => void;
    changeGroupMaterial: (params: { groupObjects: Array<string> }, callback: ApiCallback<any>) => void;
    changeMaterial: (params: { parts: Array<string>; material: string }, callback: ApiCallback<any>) => void;
    changeMaterialColor: (params: { material: string; color: string }, callback: ApiCallback<any>) => void;
    changeMaterialOptions: (
        params: { material: string; options: IMaterialOptions },
        callback: ApiCallback<any>,
    ) => void;
    changeMaterials: (params: { partObjects: IPartObjects }, callback: ApiCallback<any>) => void;
    changePartsMaterialOptions: Function; // delete? method not found in documentation
    changeViewportBackgroundColor: Function; // delete? method not found in documentation
    createFeedbackMarker: (params: {
        name: string;
        targetName?: string;
        targetType?: string;
        material?: string;
        position?: Array<any>;
        radius?: number;
        radiusMode?: string;
        color?: string;
    }) => void;
    deactivateDecorationFromParts: PartsFunction;
    dispose: CallbackOnlyFunction;
    enableAnalitycsCollection: EnablerFunction;
    enableAutoRotate: EnablerFunction;
    enableCameraControls: EnablerFunction;
    enableObjectControl: EnablerFunction;
    getARInfo: () => void; // delete? method not found in documentation
    getAllFeedbackMarkers: CallbackOnlyFunction;
    getAnnotationPosition: NameFunction;
    getAnnotationsWithMaterial: MaterialFunction;
    getAssetsInfo: CallbackOnlyFunction;
    getAvailableAnnotations: CallbackOnlyFunction;
    getAvailableCameras: CallbackOnlyFunction;
    getAvailableCustomParts: CallbackOnlyFunction;
    getAvailableMaterials: CallbackOnlyFunction<IMaterial[]>;
    getAvailableModifiers: CallbackOnlyFunction;
    getAvailableOverlays: CallbackOnlyFunction<IOverlay[]>;
    getAvailableParts: CallbackOnlyFunction;
    getAvailableTransitions: CallbackOnlyFunction<ITransition[]>;
    getCameraPosition: CallbackOnlyFunction;
    getCameraTarget: CallbackOnlyFunction;
    getDistributionInfo: DistributionInfoFunction;
    getHiddenParts: CallbackOnlyFunction;
    getLatestDistributionInfo: DistributionInfoFunction;
    getMaterial: PartsFunction;
    getModelPosition: NameFunction;
    getPartCenter: NameFunction;
    getPartOverlays: PartsFunction;
    getPartPosition: NameFunction;
    getPartsWithMaterial: MaterialFunction;
    getPerformanceData: CallbackOnlyFunction;
    getSelectable: CallbackOnlyFunction;
    getSelectableParts: CallbackOnlyFunction;
    getSelected: CallbackOnlyFunction;
    getSelectedAnnotations: CallbackOnlyFunction;
    getSelectedParts: CallbackOnlyFunction;
    getSnapshot: (params: { width: number; height: number; result?: string }, callback: ApiCallback<any>) => void;
    getViewer3D: () => void;
    getVisibleParts: CallbackOnlyFunction;
    hideAnnotations: AnnotationsFunction;
    hideCustomParts: (params: { customParts: Array<string> }, callback: ApiCallback<any>) => void;
    hideLights: LightsFunction;
    hideModels: ModelsFunction;
    hideParts: PartsFunction;
    init: (
        options: IThreediumInitOptions,
        configuration: {},
        callback: (error: IThreediumError | null, results: object[]) => void,
    ) => void;
    updateCameraToScene?: (
        props: { distanceFactor: number },
        callback: (error: IThreediumError | null, results: object[]) => void,
    ) => void;
    setCameraOptions?: (
        props: { fov: number },
        callback: (error: IThreediumError | null, results: object[]) => void,
    ) => void;
    isActiveCamera: NameFunction;
    isActiveCameraControl: NameFunction;
    isActiveProfile: NameFunction;
    isActiveRenderer: NameFunction;
    isActiveViewport: NameFunction;
    isFeedbackMarker: NameFunction;
    isPartSelected: NameFunction;
    isPartVisible: (params: { part: string }, callback: ApiCallback<any>) => void;
    isVisibleLight: NameFunction;
    removeEntryFromOverlay: OverlayEntryFunction;
    removeFeedbackMarker: NameFunction;
    removeOverlayFromPart: (params: { overlay: string; part: string }, callback: ApiCallback<any>) => void;
    render: CallbackOnlyFunction;
    resetAssetsInfo: CallbackOnlyFunction;
    resetCameraPosition: NameFunction;
    resetCameraTarget: CallbackOnlyFunction;
    selectAllPartsExcept: PartsFunction;
    selectParts: PartsFunction;
    setAllSelectable: CallbackOnlyFunction;
    setAnnotationTransformationProperties: OptionsFunction;
    setAutoRotateSpeed: (params: { speed: number }, callback: ApiCallback<any>) => void;
    setCameraPosition: (params: { position: Array<number> }, callback: ApiCallback<any>) => void;
    setCameraTarget: (params: { target: Array<number> }, callback: ApiCallback<any>) => void;
    setModelPosition: NameFunction;
    setOverlayToPart: OverlayEntryFunction;
    setPartTransformationProperties: (
        params: { name: string; position?: Array<number>; scale?: Array<number> },
        callback: ApiCallback<any>,
    ) => void;
    setPointerMode: (params: { mode: string }, callback: ApiCallback<any>) => void;
    setSelectable: PartNamesFunction;
    setViewportTransparent: (params: { viewport: string }, callback: ApiCallback<any>) => void;
    showAllParts: CallbackOnlyFunction;
    showAnnotations: AnnotationsFunction;
    showCustomParts: CustomPartObjects;
    showLights: LightsFunction;
    showModels: ModelsFunction;
    showOnlyAnnotations: (params: { annotationObjects: Array<string> }) => void;
    showOnlyCustomParts: CustomPartObjects;
    showOnlyLights: LightsFunction;
    showOnlyModels: ModelsFunction;
    showOnlyParts: PartsObjectFunction;
    showParts: PartsObjectFunction;
    switchMaterials: (params: { oldMaterial: string; newMaterial: string }, callback: ApiCallback<any>) => void;
    updateCameraControl: (
        params: {
            name: string;
            minAzimuthAngle?: number;
            maxAzimuthAngle?: number;
            minPolarAngle?: number;
            maxPolarAngle?: number;
            panSpeed?: number;
            minDistance?: number;
            maxDistance?: number;
        },
        callback: ApiCallback<any>,
    ) => void;
    updateObjectControls: (
        params: {
            name: string;
            mode?: string;
            maxDistance?: number;
            minDistance?: number;
            zoomEnabled?: boolean;
            zoomSpeed?: number;
            rotationSpeed?: number;
            rotationSpeedTouch?: number;
            horizontalRotationEnabled?: boolean;
            verticalRotationEnabled?: boolean;
        },
        callback: ApiCallback<any>,
    ) => void;
    updateOverlay: (
        params: { overlay: string; overlayEntry: string; options: IImageOverlayOptions | ITextOverlayOptions },
        callback: ApiCallback<any>,
    ) => void;
}

export interface IThreediumTemplate {
    distID: string;
    solution3DName: string;
    projectName: string;
    solution3DID: string;
    version: string;
    containerID?: string;
    threedium_id?: string;
}
